import type { Application } from './Application';
import type { Scene, SceneConstructor, SceneData, SceneContext } from './Scene';
import type { InputManager } from './InputManager';
import type { Camera } from './Camera';
import { EventEmitter } from './EventEmitter';

/**
 * Manages scene lifecycle, transitions, and updates.
 */
export class SceneManager {
  private readonly app: Application;
  private readonly input: InputManager;
  private readonly camera: Camera;
  private readonly registry: Map<string, SceneConstructor> = new Map();
  private readonly scenes: Map<string, Scene> = new Map();
  private readonly activeScenes: Set<string> = new Set();
  public readonly events: EventEmitter = new EventEmitter();

  constructor(app: Application, input: InputManager, camera: Camera) {
    this.app = app;
    this.input = input;
    this.camera = camera;

    // Set up the update loop
    this.app.ticker.add(this.update, this);
  }

  /**
   * Register a scene class for later instantiation
   */
  register(key: string, SceneClass: SceneConstructor): this {
    this.registry.set(key, SceneClass);
    return this;
  }

  /**
   * Start a scene (stops current main scene)
   */
  async start(key: string, data?: SceneData): Promise<Scene> {
    // Stop all non-overlay scenes
    for (const activeKey of this.activeScenes) {
      const scene = this.scenes.get(activeKey);
      if (scene) {
        this.stopScene(activeKey);
      }
    }

    return this.launchScene(key, data);
  }

  /**
   * Launch a scene as an overlay (keeps other scenes running)
   */
  async launch(key: string, data?: SceneData): Promise<Scene> {
    return this.launchScene(key, data);
  }

  /**
   * Stop a scene
   */
  stop(key: string): void {
    this.stopScene(key);
  }

  /**
   * Get a scene by key
   */
  getScene<T extends Scene>(key: string): T | undefined {
    return this.scenes.get(key) as T | undefined;
  }

  /**
   * Check if a scene is active
   */
  isActive(key: string): boolean {
    return this.activeScenes.has(key);
  }

  /**
   * Internal: Launch a scene
   */
  private async launchScene(key: string, data?: SceneData): Promise<Scene> {
    let scene = this.scenes.get(key);

    if (!scene) {
      // Create scene if not exists
      const SceneClass = this.registry.get(key);
      if (!SceneClass) {
        throw new Error(`Scene "${key}" not registered`);
      }

      scene = new SceneClass(key);

      const context: SceneContext = {
        app: this.app,
        sceneManager: this,
        input: this.input,
        camera: this.camera,
      };

      scene.init(context);
      this.scenes.set(key, scene);

      // Add to stage
      this.app.stage.addChild(scene);

      // Set data before create
      if (data) {
        scene.setData(data);
      }

      // Call create
      await scene.create();
    } else {
      // Scene already exists, just reactivate
      if (data) {
        scene.setData(data);
      }
      this.app.stage.addChild(scene);
    }

    scene._setActive(true);
    scene._setVisible(true);
    this.activeScenes.add(key);

    return scene;
  }

  /**
   * Internal: Stop a scene
   */
  private stopScene(key: string): void {
    const scene = this.scenes.get(key);
    if (!scene) return;

    scene._setActive(false);
    scene.shutdown();
    scene.removeFromParent();
    this.activeScenes.delete(key);
  }

  /**
   * Update loop - calls update on all active scenes
   */
  private update(): void {
    const delta = this.app.ticker.deltaMS;

    // Update camera first
    this.camera.update(delta);

    // Update all active scenes
    for (const key of this.activeScenes) {
      const scene = this.scenes.get(key);
      if (scene && scene.isActive) {
        scene.update(delta);
      }
    }
  }

  /**
   * Destroy the scene manager
   */
  destroy(): void {
    this.app.ticker.remove(this.update, this);

    for (const scene of this.scenes.values()) {
      scene.destroyScene();
    }

    this.scenes.clear();
    this.activeScenes.clear();
    this.registry.clear();
    this.events.removeAllListeners();
  }
}
